import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft, ShieldCheck, Sparkles, Minus, Plus } from "lucide-react";
import { getProduct, getProducts } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setQty(1);
    getProduct(slug)
      .then((p) => {
        setProduct(p);
        getProducts({ category: p.category }).then((all) =>
          setRelated(all.filter((x) => x.slug !== p.slug).slice(0, 3))
        );
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-[140px] pb-40 text-center">
        <p className="font-display text-3xl text-ink">Product not found</p>
        <Link to="/shop" className="text-forest font-semibold mt-4 inline-block">Back to shop</Link>
      </div>
    );
  }

  if (!product) {
    return <div className="pt-[140px] pb-40 max-w-7xl mx-auto px-5 md:px-8 animate-pulse"><div className="h-96 bg-sand rounded-2xl" /></div>;
  }

  const add = () => {
    addItem(product, qty);
    toast.success(`${qty} × ${product.name} added to cart`);
  };

  return (
    <div data-testid="product-detail-page" className="bg-bone pt-[72px] min-h-screen">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10 md:py-14">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-stone hover:text-ink transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="rounded-[1.75rem] overflow-hidden bg-sand aspect-[4/5] lg:sticky lg:top-24 self-start"
          >
            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
          </motion.div>

          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-forest">{product.category}</span>
            <h1 className="font-display text-4xl md:text-5xl tracking-tight text-ink mt-3 leading-[1.05]">{product.name}</h1>
            <p className="text-stone mt-2 text-lg">{product.subtitle}</p>
            <p className="font-display text-3xl text-ink mt-6">${product.price.toFixed(2)}</p>

            <p className="text-stone leading-relaxed mt-6 text-lg">{product.description}</p>

            <div className="mt-8 space-y-3">
              {product.benefits.map((b) => (
                <div key={b} className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-sage flex items-center justify-center flex-shrink-0">
                    <Check className="w-3.5 h-3.5 text-forest" strokeWidth={2.5} />
                  </span>
                  <span className="text-ink">{b}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-xl bg-sand border border-line p-5">
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone">Suggested protocol</p>
              <p className="text-ink mt-1.5">{product.protocol}</p>
            </div>

            <div className="mt-8 flex items-center gap-4">
              <div className="inline-flex items-center border border-line rounded-full">
                <button data-testid="qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:text-forest transition-colors">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-10 text-center font-semibold">{qty}</span>
                <button data-testid="qty-inc" onClick={() => setQty((q) => q + 1)} className="p-3 hover:text-forest transition-colors">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <button
                data-testid="add-to-cart-btn"
                onClick={add}
                className="flex-1 rounded-full bg-forest text-[#14100F] py-3.5 px-8 text-base font-semibold hover:bg-forest-dark active:scale-[0.99] transition-[background-color,transform] duration-200"
              >
                Add to cart · ${(product.price * qty).toFixed(2)}
              </button>
            </div>

            <div className="mt-6 flex items-center gap-2 text-sm text-stone">
              <ShieldCheck className="w-4 h-4 text-forest" strokeWidth={1.5} />
              Every order is reviewed by a licensed provider before shipping.
            </div>

            <button
              onClick={() => navigate("/intake")}
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-forest hover:gap-3 transition-all"
            >
              <Sparkles className="w-4 h-4" strokeWidth={1.5} /> Not sure this is right? Ask our AI
            </button>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-24">
            <h2 className="font-display text-3xl md:text-4xl tracking-tight text-ink mb-8">You may also like</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {related.map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
